import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from 'api/apiClient';
import { notify } from 'components/Common/Toast';
import { RootState } from 'redux/store';
import { Announcement, AnnouncementState } from 'types/announcements';
import { getDefaultCompanyId, sortByField } from 'utils/helpers';

const initialState: AnnouncementState = {
    data: [],
    loading: false,
    error: false,
    totalCount: 0,
};

export const normalizeAnnouncement = (item: Announcement) => {
    const data: any = item?.data;
    let normalizedData: any;
    try {
        normalizedData = JSON.parse(data ?? '') || {};
    } catch (e) {
        normalizedData = data;
    }

    return { ...item, data: normalizedData };
};

export const deleteAnnouncementById = createAsyncThunk('announcements/deleteAnnouncementById', async (id: number, { dispatch }) => {
    const res: any = await client.delete(`/notification/${id}`);
    if (res?.code === 200) {
        notify('Announcement deleted successfully', 'success');
        dispatch(fetchAnnouncements());
    }
    return res;
});

export const fetchAnnouncements = createAsyncThunk('announcements/fetchAnnouncements', async (_, { getState }) => {
    const state = getState() as RootState;
    const companyId = getDefaultCompanyId(state);
    const { pageNumber, pageSize } = state.app.table;
    const res: any = await client.post(
        '/notification/pagination',
        {},
        {
            notificationStatus: 'ALL',
            pageSize,
            companyId,
            pageNumber,
        }
    );
    if (res.code === 200) {
        const { totalCount, notifications: _notifications } = res || {};
        // mapping each notification because backend is retuning data key in json format
        let notifications = [...(_notifications || [])];
        notifications = notifications.map(normalizeAnnouncement);
        return { notifications: notifications as Announcement[], totalCount };
    }
});

const announcementsSlice = createSlice({
    name: 'announcements',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchAnnouncements.pending, (state) => {
            state.loading = true;
            state.error = false;
        });
        builder.addCase(fetchAnnouncements.fulfilled, (state, action) => {
            state.loading = false;
            const { payload } = action || {};
            if (!payload) return;
            const { notifications, totalCount } = payload || {};
            const sortedData = sortByField(notifications, 'createDate', 'DESC') || [];
            state.data = sortedData;
            state.totalCount = totalCount;
        });
        builder.addCase(fetchAnnouncements.rejected, (state) => {
            state.loading = false;
            state.error = true;
        });
    },
});

export default announcementsSlice.reducer;
